import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { DeviceService, IProductCard } from '@seb/web-design-system/core';

import { ConfigurationService } from './configuration.service';
import { ComparedProductCard } from '../models';

@Injectable({
  providedIn: 'root'
})
export class ProductComparatorService implements OnDestroy {
  static readonly DEFAULT_MAX_DESKTOP = 4;
  static readonly DEFAULT_MAX_MOBILE = 2;

  private readonly destroy$: Subject<void> = new Subject();

  private readonly products = new BehaviorSubject<ComparedProductCard[]>([]);
  private readonly maxProducts = new BehaviorSubject<number>(ProductComparatorService.DEFAULT_MAX_DESKTOP);
  private readonly maxLengthError = new Subject<void>();
  private readonly wrongCategoryError = new Subject<ComparedProductCard>();

  get maxProducts$(): Observable<number> {
    return this.maxProducts.asObservable();
  }

  get products$(): Observable<ComparedProductCard[]> {
    return this.products.asObservable();
  }

  get maxLengthError$(): Observable<void> {
    return this.maxLengthError.asObservable();
  }

  get wrongCategoryError$(): Observable<ComparedProductCard> {
    return this.wrongCategoryError.asObservable();
  }

  constructor(
    private deviceService: DeviceService,
    @Inject(PLATFORM_ID) private platformId: any,
    configService: ConfigurationService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      combineLatest([configService.getConfig(), this.deviceService.isDevice])
        .pipe(
          map(([config, isDevice]) => {
            return isDevice
              ? config.components?.comparator?.maxProducts?.mobile || ProductComparatorService.DEFAULT_MAX_MOBILE
              : config.components?.comparator?.maxProducts?.desktop || ProductComparatorService.DEFAULT_MAX_DESKTOP;
          }),
          takeUntil(this.destroy$)
        )
        .subscribe((maxProducts) => {
          this.maxProducts.next(maxProducts);
        });

      const staticComparatorProducts = JSON.parse(localStorage.getItem('storedComparatorProducts'));
      if (staticComparatorProducts) {
        this.products.next(staticComparatorProducts);
      } else {
        this.products.next([]);
      }
      this.products
        .pipe(takeUntil(this.destroy$))
        .subscribe((p) => localStorage.setItem('storedComparatorProducts', JSON.stringify(p)));
    }
  }

  addProduct(productCard: IProductCard, productCategories?: any[]): boolean {
    const comparedProduct: ComparedProductCard = { ...productCard };
    const currentProducts = this.products.getValue();
    if (currentProducts.length === 0) {
      currentProducts.push(comparedProduct);
      this.products.next(currentProducts);
      return true;
    }
    const currentMax = this.maxProducts.getValue();
    const foundCode1 = productCategories?.some((item) => item.code === currentProducts[0].code);
    const foundCode2 = productCategories?.some((item) => item.code === productCard.code);

    if (currentProducts.length === currentMax) {
      this.maxLengthError.next();
      return false;
    }

    let commonValues = false;
    if (foundCode1 && foundCode2) {
      const code1Values = productCategories.find((item) => item.code === currentProducts[0].code);
      const code2Values = productCategories.find((item) => item.code === productCard.code);

      commonValues = Object.values(code1Values).some((value) => Object.values(code2Values).includes(value));
    }

    if (commonValues) {
      currentProducts.push(comparedProduct);
      this.products.next(currentProducts);
      return true;
    } else {
      this.wrongCategoryError.next(comparedProduct);
      return false;
    }
  }

  removeProduct(productCode: string): void {
    const currentProducts = this.products.getValue();
    const toDeleteIndex = currentProducts.findIndex((product) => product.code === productCode);
    if (toDeleteIndex > -1) {
      currentProducts.splice(toDeleteIndex, 1);
      this.products.next(currentProducts);
    }
  }

  emptyProducts(): void {
    this.products.next([]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
